import React from 'react';
import { Player, GameState, GamePhase, CardColor, CardType } from '../types';
import { Language, t } from '../data/translations';
import AstridsArrows from '../assets/ui/classic/Astrids_Arrows.png';

import { useShellLayout } from '../hooks/useShellLayout';

interface HeroAbilityHUDProps {
    player: Player;
    gameState: GameState;
    setTargeting: (t: any) => void;
    setMousePos: (pos: { x: number; y: number }) => void;
    playerAvatarRef: React.RefObject<HTMLDivElement>;
    language: Language;
}

const HeroAbilityHUD: React.FC<HeroAbilityHUDProps> = ({
    player,
    gameState,
    setTargeting,
    setMousePos,
    playerAvatarRef,
    language
}) => {
    const { isMobileShell } = useShellLayout();
    const colorHexMap: Record<CardColor, string> = {
        [CardColor.Red]: '#ef4444', [CardColor.Pink]: '#ec4899', [CardColor.Green]: '#22c55e',
        [CardColor.Black]: '#475569', [CardColor.Purple]: '#a855f7', [CardColor.Yellow]: '#eab308',
    };

    const astridCharges = player.astridCharges || {};
    const readyColors = (Object.keys(astridCharges) as CardColor[]).filter(color => Math.floor((astridCharges[color] ?? 0) / 3) > 0);
    const isCharged = readyColors.length > 0 && gameState.activations.length === 0;

    const handleUnifiedStart = (e: React.MouseEvent<HTMLDivElement> | React.TouchEvent<HTMLDivElement>) => {
        if (!isCharged) return;
        const color = readyColors[0];
        const rect = playerAvatarRef.current?.getBoundingClientRect();
        if (!rect) return;
        const preparedLike = { card: { id: 'ASTRID_BLAST', type: CardType.Attack, value: 2, color } as any, rotation: 0 } as any;
        setTargeting({ card: preparedLike, startRect: rect, source: 'astrid-hero' } as any);
        
        const clientX = 'touches' in e ? e.touches[0].clientX : (e as React.MouseEvent).clientX;
        const clientY = 'touches' in e ? e.touches[0].clientY : (e as React.MouseEvent).clientY;
        setMousePos({ x: clientX, y: clientY });
    };

    return (
        <>
            {player.hero === 'ASTRID' && (gameState.phase === GamePhase.Combat || gameState.phase === GamePhase.TowerPurchase) && (
                <div id="hero-hud-astrid" className="relative flex flex-col items-center justify-center gap-0 mx-[4px]" style={{ transform: isMobileShell ? 'translateX(-20px)' : 'translateX(-40px)' }}>
                    <div
                        id="astrid-arrows-button"
                        className={`absolute z-50 w-[80px] h-[80px] transition-all duration-300 flex items-center justify-center select-none
                            ${isCharged ? 'scale-105 drop-shadow-[0_0_12px_rgba(239,68,68,0.7)] animate-pulse cursor-pointer' : 'opacity-40 grayscale pointer-events-none'}`}
                        style={{
                            top: '-85px',
                            left: isMobileShell ? '-115px' : '-55px',
                            zIndex: isMobileShell ? 30 : 50
                        }}
                        onMouseDown={handleUnifiedStart}
                        onTouchStart={(e) => { e.preventDefault(); handleUnifiedStart(e); }}
                    >
                        <img src={AstridsArrows} alt="Astrid Blast" className="w-full h-full object-contain" />
                        {isCharged && <div className="absolute inset-0 rounded-full border-4 border-red-500/20 blur-md animate-ping" />}
                    </div>

                    <div className="flex flex-col items-center justify-center gap-0 z-10 relative mb-4">
                        {[[CardColor.Red, CardColor.Pink, CardColor.Green], [CardColor.Black, CardColor.Purple, CardColor.Yellow]].map((row, rIdx) => (
                        <div key={`astrid-row-${rIdx}`} className="flex flex-row items-center justify-center gap-2">
                            {row.map(color => {
                                const charges = (player.astridCharges?.[color] ?? 0);
                                const fullSets = Math.floor(charges / 3);
                                const remainder = charges % 3;
                                const canActivate = fullSets > 0 && gameState.activations.length === 0;

                                    return (
                                        <div key={`astrid-${color}`} className="mx-[2px] flex flex-col items-center select-none w-[34px]">
                                            <div className="text-[10px] mb-[2px]" style={{ color: colorHexMap[color] }}>{t(`term.${color.toLowerCase()}` as any, language)}</div>
                                            <div className="flex flex-row gap-[1px] p-[2px] rounded" style={{ border: `1px solid ${colorHexMap[color]}` }}>
                                                {Array.from({ length: 3 }).map((_, lineIdx) => {
                                                    const filled = fullSets > 0 ? true : (lineIdx < remainder);
                                                    return (
                                                        <div key={lineIdx} className="w-[4px] h-5 rounded-sm" style={{ background: filled ? colorHexMap[color] : 'rgba(100,116,139,0.4)', boxShadow: filled ? `0 0 6px ${colorHexMap[color]}` : undefined }} />
                                                    );
                                                })}
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {player.hero === 'NORIM' && (
                <div className="flex flex-col items-center justify-center gap-0 mx-[4px]" style={{ transform: 'translateX(-40px)' }}>
                    <div className="text-[10px] mb-[2px] text-blue-400 font-bold uppercase">{t('term.essence', language)}</div>
                    <div className="flex flex-row gap-[2px] p-[3px] rounded border border-blue-500 bg-slate-900/50">
                        {Array.from({ length: 3 }).map((_, i) => {
                            const filled = (player.norimCharges || 0) > i;
                            return (
                                <div key={`norim-charge-${i}`} className="w-[6px] h-[30px] rounded-sm transition-all duration-300" style={{
                                    background: filled ? '#3b82f6' : 'rgba(30, 58, 138, 0.4)',
                                    boxShadow: filled ? '0 0 8px #3b82f6' : 'none',
                                    border: filled ? '1px solid #60a5fa' : '1px solid rgba(30, 58, 138, 0.6)'
                                }} />
                            );
                        })}
                    </div>
                </div>
            )}
        </>
    );
};

export default HeroAbilityHUD;
